/** Heure stylisée — même geste que FxDate, créneaux de 15 min (Paris). */
import { useEffect, useRef, useState } from "react";
import { snapQuarterHour } from "../payments/types";
import "./fxCtrl.css";

const QUARTERS = [0, 15, 30, 45];

type FxTimeProps = {
  id?: string;
  value: string;
  disabled?: boolean;
  onChange: (hhmm: string) => void;
};

function parts(hhmm: string): { h: number; m: number } {
  const [hs, ms] = snapQuarterHour(hhmm, "06:15").split(":");
  return { h: Number(hs), m: Number(ms) };
}

export function timeLabel(hhmm: string): string {
  const { h, m } = parts(hhmm);
  return `${String(h).padStart(2, "0")} h ${String(m).padStart(2, "0")}`;
}

export function FxTime({ id, value, disabled, onChange }: FxTimeProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const cur = parts(value);

  function place() {
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: r.bottom + 6, left: r.left });
  }

  useEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  function setAt(h: number, m: number, close: boolean) {
    onChange(snapQuarterHour(`${h}:${String(m).padStart(2, "0")}`, value));
    if (close) setOpen(false);
  }

  return (
    <div ref={wrap} className={open ? "fx-ctrl fx-time is-open" : "fx-ctrl fx-time"}>
      <button
        ref={btn}
        id={id}
        type="button"
        className="fx-ctrl__btn"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span className="fx-ctrl__ico" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <circle cx="12" cy="12" r="8.5" />
            <path d="M12 7.5V12l3.2 2" />
          </svg>
        </span>
        <span className={value ? "fx-ctrl__lab" : "fx-ctrl__lab is-empty"}>{value ? timeLabel(value) : "Choisir"}</span>
        <span className="fx-ctrl__chev" aria-hidden>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {open ? (
        <div className="fx-ctrl__panel fx-time__panel" role="dialog" style={{ top: pos.top, left: pos.left }}>
          <p className="fx-time__cap">Heure</p>
          <div className="fx-time__grid">
            {Array.from({ length: 24 }, (_, h) => (
              <button
                key={h}
                type="button"
                className={h === cur.h ? "is-on" : ""}
                onClick={() => setAt(h, cur.m, false)}
              >
                {String(h).padStart(2, "0")}
              </button>
            ))}
          </div>
          <p className="fx-time__cap">Minutes</p>
          <div className="fx-time__mins">
            {QUARTERS.map((m) => (
              <button
                key={m}
                type="button"
                className={m === cur.m ? "is-on" : ""}
                onClick={() => setAt(cur.h, m, true)}
              >
                {String(m).padStart(2, "0")}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
